import { createFileRoute, getRouteApi, useNavigate } from "@tanstack/react-router";
import { asSearchString } from "@repo/utils/search-params";
import { useCallback } from "react";
import { toast } from "@heroui/react";
import { useAuth } from "@repo/context/auth/auth";
import TrustedQueryPanel from "@repo/ui/pages/template_service/trusted-query/TrustedQueryPanel";
import HistoryRecordItem from "@repo/ui/pages/template_service/history/HistoryRecordItem";
import { useSendReceiveHistory } from "@repo/ui/pages/template_service/history/useSendReceiveHistory";
import { SafeAreaContainer } from "@/components/layout/safe-area-container";

export type TrustedQuerySearch = {
  search?: string;
};

export const Route = createFileRoute("/template_protections/trusted-query")({
  validateSearch: (search: Record<string, unknown>): TrustedQuerySearch => ({
    search: asSearchString(search.search),
  }),
  component: TrustedQueryPage,
});

const trustedQueryRouteApi = getRouteApi("/template_protections/trusted-query");

function HistoryFlatList({ uid, queryToken }: { uid: string; queryToken: number }) {
  const navigate = useNavigate();
  const onError = useCallback((message: string) => {
    toast.danger(message);
  }, []);

  const { items, loading, resolveParty } = useSendReceiveHistory({
    type: "recive",
    receiver_id: uid,
    pageSize: 50,
    maxCount: 500,
    enabled: !!uid,
    onError,
  });

  // remount when queryToken changes via key on parent
  void queryToken;

  if (loading && items.length === 0) {
    return <div className="py-8 text-center text-sm text-gray-500">加载中...</div>;
  }

  if (items.length === 0) {
    return <div className="py-8 text-center text-sm text-gray-500">暂无收货历史</div>;
  }

  return (
    <div>
      {items.map((item) => (
        <HistoryRecordItem
          key={`${item.send_id}-${item.template_id}-${item.received_at}`}
          item={item}
          type="recive"
          party={resolveParty(item)}
          onUserPress={(partyUid) =>
            navigate({
              to: "/template_protections/trusted-query",
              search: { search: partyUid },
            })
          }
          onTemplatePress={(templateId, sendId) =>
            navigate({
              to: "/template_protections/detail/$templateId",
              params: { templateId },
              search: { sendId: sendId != null ? String(sendId) : undefined },
            })
          }
        />
      ))}
    </div>
  );
}

export default function TrustedQueryPage() {
  const { search } = trustedQueryRouteApi.useSearch();
  const navigate = trustedQueryRouteApi.useNavigate();
  const { isAuthenticated, userInfo } = useAuth();

  const setSearch = useCallback(
    (value: string) => {
      void navigate({ search: (prev) => ({ ...prev, search: value || undefined }) });
    },
    [navigate],
  );

  return (
    <SafeAreaContainer className="bg-surface" customPaddingBottom="2rem">
      <div className="px-4 pt-3 pb-6">
        <TrustedQueryPanel
          search={search}
          onSearchChange={setSearch}
          isAuthenticated={!!isAuthenticated}
          userInfoUid={userInfo?.uid}
          onLoginRequired={() => toast.danger("请重新从 App 打开本页面")}
          onNavigateSelfBlacklist={() =>
            navigate({ to: "/template_protections/blacklist/self" })
          }
          onNavigatePublicBlacklist={(id, searchUid) =>
            navigate({
              to: "/template_protections/blacklist/public/$id",
              params: { id },
              search: searchUid ? { search_uid: searchUid } : {},
            })
          }
          renderHistory={(uid, queryToken) => (
            <HistoryFlatList key={`${uid}-${queryToken}`} uid={uid} queryToken={queryToken} />
          )}
        />
      </div>
    </SafeAreaContainer>
  );
}
